import './styles.scss'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_COMPONENT, SHAPE_FACTSHEET_BLUEPRINT } from '../shapes'
import { getAttributeRequired, getComponentLabel, getTrackingFactLabel } from '../helper'
import { useSelector } from 'react-redux'
import { FACT_ATTRIBUTE_IDS, FACT_IDS } from '../constants'
import CurrencySelectorOverlay from '../CurrencySelectorOverlay'
import { factsheetValidator } from '../validator'
import InputTracked from '../../FormTracked/InputTracked'
import { trackingEvent } from '../../../../constants'
import SelectTracked from '../../FormTracked/SelectTracked'

/**
 * This component renders the price input based on the blueprints received.
 * If only price input blueprint is supplies, a normal input is rendered.
 * Supplying more than ones introduces a select dropdown to pick a pricing attribute as well.
 */
const PriceInput = ({
                      priceAttributeBlueprints,
                      selectedBlueprint,
                      parentBlueprint,
                      factsheetBlueprint,
                      factInstanceKey,
                      data,
                      collapsed,
                      putAttribute,
                      deleteAttribute,
                      putValidationStatus,
                      onChangeBlueprint,
                    }) => {
  const [isValid, setIsValid] = useState(true)
  const [showCurrencySelector, setShowCurrencySelector] = useState(false)
  const factDefId = parentBlueprint.id
  const fact = factsheetBlueprint.factTree.facts[factDefId]
  const attributeDefId = selectedBlueprint.id
  const attribute = factsheetBlueprint.factTree.attributes[attributeDefId]
  const required = useMemo(() => getAttributeRequired(selectedBlueprint, attribute, fact), [
    selectedBlueprint,
    attribute,
    fact,
  ])
  const currencyCodeValue = useSelector(state => {
    if (!state.factsheet.facts[FACT_IDS.CURRENCY]) {
      return null
    }
    return state.factsheet.facts[FACT_IDS.CURRENCY][0]?.attributes[FACT_ATTRIBUTE_IDS.CURRENCY_CODE]?.value
  })

  const validateValue = useCallback((value) => {
    return value === '' ? !required.attribute : factsheetValidator.validateValue(value, attribute).valid
  }, [required, attribute])

  const onInputFocus = useCallback((e) => {
    if (!currencyCodeValue) {
      e.target.blur()
      setShowCurrencySelector(true)
    }
  }, [currencyCodeValue])

  const onPricingSelect = useCallback((e) => {
    const newBlueprintId = e.target.value
    const otherIds = priceAttributeBlueprints
      .filter((b) => b.id !== newBlueprintId)
      .map((b) => b.id)
    if (data?.value) {
      // carry the value onto the newly selected blueprint and drop the others in one
      // atomic set, so the contradiction middleware can hold or undo it as a unit
      // rather than committing the deletes while holding the put.
      putAttribute(
        parentBlueprint.id,
        factInstanceKey,
        newBlueprintId,
        {
          attributeDefId: newBlueprintId,
          value: data.value,
        },
        undefined,
        otherIds
      )
    } else {
      // nothing to set -> the deletes alone can't introduce a conflict, so a plain
      // delete per other blueprint is safe. With the put above, the parent re-deduces the
      // selected blueprint from redux once the set is applied, so a held/cancelled set
      // leaves the selection untouched; with no value there's no put, so switch locally.
      const selected = priceAttributeBlueprints.find((b) => b.id === newBlueprintId)
      selected && onChangeBlueprint && onChangeBlueprint(selected)
      otherIds.forEach((id) => deleteAttribute(parentBlueprint.id, factInstanceKey, id))
    }
  }, [
    priceAttributeBlueprints,
    parentBlueprint.id,
    factInstanceKey,
    deleteAttribute,
    putAttribute,
    data,
    onChangeBlueprint,
  ])

  const onValueChange = useCallback(e => {
      const newValue = e.target.value
      if (newValue === '') {
        deleteAttribute(
          parentBlueprint.id,
          factInstanceKey,
          selectedBlueprint.id
        )
        return
      }
      putAttribute(
        parentBlueprint.id,
        factInstanceKey,
        selectedBlueprint.id,
        {
          attributeDefId: selectedBlueprint.id,
          value: newValue,
        }
      )
    },
    [
      deleteAttribute,
      putAttribute,
      selectedBlueprint.id,
      parentBlueprint.id,
      factInstanceKey
    ])

  useEffect(() => {
    const validationStatus = validateValue(data?.value ?? '')
    setIsValid(validationStatus)
    putValidationStatus(
      parentBlueprint.id,
      factInstanceKey,
      selectedBlueprint.id,
      validationStatus
    )
  }, [
    validateValue,
    data,
    putValidationStatus,
    parentBlueprint.id,
    factInstanceKey,
    selectedBlueprint.id,
  ])

  const trackingEventOpts = useMemo(() => {
    return { name: getTrackingFactLabel(parentBlueprint, factsheetBlueprint) }
  }, [parentBlueprint, factsheetBlueprint])

  const label = getComponentLabel(
    selectedBlueprint,
    attribute
  )

  if (collapsed) {
    if (!data?.value) {
      return null
    }
    return <div className={'attribute-input-collapsed'}>{label}: {`${data.value} ${currencyCodeValue}`}</div>
  }

  const options = priceAttributeBlueprints.map(b => {
    const attribute = factsheetBlueprint.factTree.attributes[b.id]
    return {
      label: getComponentLabel(b, attribute),
      value: b.id,
    }
  })

  const hasLabel = priceAttributeBlueprints.length === 1
  const containerClassNames = 'input-container' + (hasLabel ? ' has-suffix' : '')
  return (
    <div className="attribute-price-input">
      {showCurrencySelector && <CurrencySelectorOverlay
        blueprint={factsheetBlueprint}
        onDone={() => {setShowCurrencySelector(false)}}
      />}
      <div className="input-group">
        <div className={containerClassNames}>
          <span className="prefix">{currencyCodeValue || 'currency'}</span>
          <InputTracked
            eventName={trackingEvent.editValue}
            eventOpts={trackingEventOpts}
            type="number"
            value={data?.value ?? ''}
            onChange={onValueChange}
            valid={isValid}
            step={0.000000001}
            onFocus={onInputFocus}
            indicateNativeInvalidState={true}
          />
          {hasLabel && <span className="suffix">{label}</span>}
        </div>
        {
          priceAttributeBlueprints.length > 1 && <div className="select-container">
            <SelectTracked
              eventName={trackingEvent.editValue}
              eventOpts={trackingEventOpts}
              selected={selectedBlueprint.id}
              onChange={onPricingSelect}
              options={options}
            />
          </div>
        }
      </div>
    </div>
  )
}

PriceInput.propTypes = {
  priceAttributeBlueprints: PropTypes.arrayOf(PropTypes.shape(SHAPE_COMPONENT)).isRequired,
  selectedBlueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  parentBlueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  factInstanceKey: PropTypes.number.isRequired,
  data: PropTypes.object,
  collapsed: PropTypes.bool.isRequired,
  putAttribute: PropTypes.func.isRequired,
  deleteAttribute: PropTypes.func.isRequired,
  putValidationStatus: PropTypes.func.isRequired,
  onChangeBlueprint: PropTypes.func,
}

export default PriceInput
